import { withStyles, withTheme } from "@material-ui/core";
import styles from "../../styles";
import { compose } from "recompose";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import Map, {
  Marker,
  Popup,
  MapControls,
  MapConstant,
} from "../../../../../lib/Map";
import { TerminalMarker } from "../../../../../components/mapComponents/Markers";
import {
  AnchorPointPopupInfo,
  ZonePopUpInfo,
} from "../../../../../components/mapComponents/Popups";
import Events from "../../../../../utilities/EventEmitter";
import { withAppConfig } from "../../../../../appConfigContext";
import appConstants from "@appConstants";
import { getAnchorPointMarker } from "../../../IBPlanning/RoutesView/RouteMap/ShowAnchorPoints/utils";
import { MapLegend } from "../../../../../layout/layoutHeader/components";
import MapHeader from "./MapHeader";
import { legendItems } from "./constants";
import { observer, inject } from "mobx-react";

@inject('CommonStore')
@observer
class AnchorPointMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAutoZoomEnabled: false,
      isMapLegendEnabled: false,
      selectedAnchorPoint: this.getSelectedAnchorPoint()
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.selectedAnchorPoints !== this.props.selectedAnchorPoints ||
      prevProps.CommonStore.filteredAnchorPointsOnMap !== this.props.CommonStore.filteredAnchorPointsOnMap
    ) {
      this.setState({
        selectedAnchorPoint: this.getSelectedAnchorPoint()
      });
    }
  }

  getSelectedAnchorPoint = () => {
    const { selectedAnchorPoints } = this.props;
    const anchorPointData = this.props.CommonStore.filteredAnchorPointsOnMap.slice();
    if (selectedAnchorPoints?.length === 1) {
      return anchorPointData.find((anchor) => anchor.anchorPointName === selectedAnchorPoints[0]) || null;
    }
    return null;
  };

  onMarkerDragEnd = (stop) => {
    this.props.anchorPointsPositionChange({
      latitude: stop.position.lat,
      longitude: stop.position.lng,
    });
  };

  toggleRouteAnimation = (value) => {
    this.setState({
      isAutoZoomEnabled: value,
    });
  };

  anchorPointMarkerParams = (anchorPoint) => {
    const isActiveAnchorPoint = this.props.selectedAnchorPoints.includes(
      anchorPoint.anchorPointName
    );
    return {
      zIndex: isActiveAnchorPoint ? 1 : 0, // used rerender icon when isSelected property is changed on active anchor point,
      isActiveAnchorPoint,
      isDisabledAnchorPoint: !anchorPoint.isSelected
    };
  };

  ShowEditAnchorPointPosition = (anchorPoint) => {
    if (
      this.props.anchorPointsPosition &&
      this.props.anchorPointsInfo?.anchorPointName ===
        anchorPoint.anchorPointName
    ) {
      return {
        lat: this.props.anchorPointsPosition.latitude,
        lng: this.props.anchorPointsPosition.longitude,
      };
    } else {
      return {
        lat: anchorPoint.latitude,
        lng: anchorPoint.longitude,
      };
    }
  };
  onMapClick = (e) => {
    if (
      this.props.isAddUpdateAnchorPointFormOpen &&
      !this.props.anchorPointsInfo &&
      !this.props.anchorPointsPosition
    ) {
      this.props.anchorPointsPositionChange({
        latitude: e.lat,
        longitude: e.lng,
      });
    }
    this.props.onAnchorPointClicked(null);
  };

  toggleMapLegend = () => {
    this.setState((prevState) => ({
      isMapLegendEnabled: !prevState.isMapLegendEnabled,
    }));
  };

  renderMapLegend = () => {
    return (
      <MapLegend 
        isEnabled={this.state.isMapLegendEnabled}
        renderIcon={({ item, theme }) => getAnchorPointMarker({
          anchorPointData: item.data,
          theme,
          isActiveAnchorPoint: item.isActive,
          isDisabledAnchorPoint: item.isDisabled
        })}
        legendItems={legendItems}
      />
    );
  };

  render() {
    const { selectedAnchorPoint } = this.state;
    const { classes, theme, zoneInfo, onZoneClicked, configJSON, isAddUpdateAnchorPointFormOpen } =
      this.props;
    const { location: terminalLocation, code: terminalID } =
      this.props.userData.userProfile.terminal;
    
    const anchorPointData = this.props.CommonStore.filteredAnchorPointsOnMap.slice();

    return (
      <div className={classes.anchorSubModulePart}>
        <MapHeader
          eventHub={Events}
          isAutoZoomEnabled={this.state.isAutoZoomEnabled}
          isMapLegendEnabled={this.state.isMapLegendEnabled}
          toggleRouteAnimation={this.toggleRouteAnimation}
          toggleMapLegend={this.toggleMapLegend}
        />
        <div className={classes.anchorPointModuleContainer}>
          <div
            className={
              isAddUpdateAnchorPointFormOpen &&
              !this.props.anchorPointsInfo &&
              !this.props.anchorPointsPosition
                ? `${classes.anchorPointContent} ${classes.anchorPointMap}`
                : classes.anchorPointContent
            }
          >
            <Map
              zoom={
                this.state.isAutoZoomEnabled &&
                isAddUpdateAnchorPointFormOpen
                  ? 15
                  : 9
              }
              initialCenter={terminalLocation}
              center={terminalLocation}
              onClick={this.onMapClick}
              mapId={appConstants.mapIds.ANCHOR_POINT_MAP}
              mapTheme={theme.themeName}
              mapConfig={configJSON?.maps}
            >
              <MapControls
                controlOptions={{
                  zoomControl: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                  },
                  satelliteViewControl: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                  },
                  reCenterMapControl: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                  },
                  showZones: {
                    position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                    terminalID: terminalID,
                    onZoneClicked: onZoneClicked,
                    geoJson: this.props.userData.userProfile?.geoJson,
                  },
                }}
              />
              <Marker
                position={terminalLocation}
                animation
                // anchor point is the point in the image which should overlap the location,
                // Terminal image is of different size than the marker so we can override the default value as shown below
                anchorPoint={{ x: 20, y: 20 }}
                zIndex={1}
                forceUpdate={true}
                isActiveShipment
              >
                <TerminalMarker
                  data={this.props.userData.userProfile.terminal}
                  color={theme.terminalContentColor}
                />
              </Marker>
              {anchorPointData?.map((anchorPoint) => {
                const params = this.anchorPointMarkerParams(anchorPoint);
                return (
                  <Marker
                    data={anchorPoint}
                    position={this.ShowEditAnchorPointPosition(anchorPoint)}
                    key={anchorPoint.anchorPointName}
                    onClick={this.props.onAnchorPointClicked}
                    draggable={Boolean(
                      params.isActiveAnchorPoint && this.props.anchorPointsInfo
                    )}
                    onDragEnd={this.onMarkerDragEnd}
                    forceUpdate={true}
                    isActiveShipment={
                      params.isActiveAnchorPoint && this.state.isAutoZoomEnabled
                    }
                    zIndex={params.zIndex} // display active shipments on top
                  >
                    {getAnchorPointMarker({
                      anchorPointData: anchorPoint,
                      theme,
                      isActiveAnchorPoint: params.isActiveAnchorPoint,
                      isDisabledAnchorPoint: params.isDisabledAnchorPoint
                    })}
                  </Marker>
                );
              })}
              {isAddUpdateAnchorPointFormOpen &&
                !this.props.anchorPointsInfo &&
                this.props.anchorPointsPosition && (
                  <Marker
                    data={{}}
                    position={{
                      lat: this.props.anchorPointsPosition.latitude,
                      lng: this.props.anchorPointsPosition.longitude,
                    }}
                    key={"new"}
                    onClick={this.props.onAnchorPointClicked}
                    draggable={true}
                    onDragEnd={this.onMarkerDragEnd}
                    zIndex={1} // display active shipments on top
                    forceUpdate={true}
                    isActiveShipment={this.state.isAutoZoomEnabled}
                  >
                    {getAnchorPointMarker({
                      anchorPointData: {},
                      theme,
                      isActiveAnchorPoint: true,
                    })}
                  </Marker>
                )}

              {selectedAnchorPoint && !isAddUpdateAnchorPointFormOpen && (
                <Popup
                  position={{
                    lat: selectedAnchorPoint.latitude,
                    lng: selectedAnchorPoint.longitude
                  }}
                  // forceRender will rerender the child component into the infowindow,
                  // set to true when theme changes or data is updated for selected shipment
                >
                  <AnchorPointPopupInfo
                     isDisabledAnchorPoint={!selectedAnchorPoint.isSelected}
                     data={{
                      anchorPointName: selectedAnchorPoint.anchorPointName,
                      anchorPointEmptyTime: selectedAnchorPoint.emptyTime,
                      routeId: selectedAnchorPoint.routeId,
                      routeAssigned: selectedAnchorPoint.routeAssigned,
                      shipper1: selectedAnchorPoint.shipper1,
                      weightPercentageShipper1:
                        selectedAnchorPoint.weightPercentageShipper1,
                      shipper2: selectedAnchorPoint.shipper2,
                      weightPercentageShipper2:
                        selectedAnchorPoint.weightPercentageShipper2,
                      lastDerivedOn: selectedAnchorPoint.lastDerivedOn,
                      avgPickup: selectedAnchorPoint.avgPickup,
                      isSelected: selectedAnchorPoint.isSelected
                    }}
                    theme={theme.themeName}
                    isAnchorPointModulePopup
                  />
                </Popup>
              )}
              {zoneInfo && (
                <Popup isCloseButton position={zoneInfo.position}>
                  <ZonePopUpInfo data={zoneInfo.data} />
                </Popup>
              )}
              {this.renderMapLegend()}
            </Map>
          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  withTheme(),
  withUserProfile,
  withAppConfig
)(AnchorPointMap);
